import { useState, useRef, useEffect, useCallback } from 'react';
import { showToast } from '../utils/toast';

export const calculateMaxHr = (age = 30) => {
    const safeAge = Math.min(95, Math.max(12, parseInt(age, 10) || 30));
    return Math.round(208 - (0.7 * safeAge));
};

export const getHrZoneInfo = (bpm, maxHr) => {
    if (!bpm || bpm <= 0 || !maxHr) {
        return { zone: 0, name: 'Wachten op signaal', pct: 0, color: 'text-gray-400', bg: 'bg-zinc-900', border: 'border-zinc-700' };
    }
    const pct = Math.round((bpm / maxHr) * 100);
    if (pct < 60) {
        return { zone: 1, name: 'Zone 1 • Herstel', pct, color: 'text-sky-400', bg: 'bg-sky-950/60', border: 'border-sky-500/40' };
    }
    if (pct < 70) {
        return { zone: 2, name: 'Zone 2 • Aerobe Duur', pct, color: 'text-emerald-400', bg: 'bg-emerald-950/60', border: 'border-emerald-500/40' };
    }
    if (pct < 80) {
        return { zone: 3, name: 'Zone 3 • Tempo', pct, color: 'text-amber-400', bg: 'bg-amber-950/60', border: 'border-amber-500/40' };
    }
    if (pct < 90) {
        return { zone: 4, name: 'Zone 4 • Threshold', pct, color: 'text-orange-500', bg: 'bg-orange-950/60', border: 'border-orange-500/50' };
    }
    return { zone: 5, name: 'Zone 5 • VO2Max Piek', pct, color: 'text-red-500', bg: 'bg-red-950/70', border: 'border-red-500/60' };
};

const OPTIONAL_BLE_SERVICES = [
    'heart_rate',
    'running_speed_and_cadence',
    'cycling_speed_and_cadence',
    'cycling_power',
    'fitness_machine',
    'battery_service',
    'device_information'
];

export const useHeartRate = (userAge = 30) => {
    const isSupported = typeof navigator !== 'undefined' && !!navigator.bluetooth;
    const maxHr = calculateMaxHr(userAge);

    const [isConnected, setIsConnected] = useState(false);
    const [isConnecting, setIsConnecting] = useState(false);
    const [deviceName, setDeviceName] = useState('');
    const [batteryLevel, setBatteryLevel] = useState(null);
    const [sensorContact, setSensorContact] = useState(true);

    const [currentBpm, setCurrentBpm] = useState(0);
    const [avgBpm, setAvgBpm] = useState(0);
    const [maxBpm, setMaxBpm] = useState(0);
    const [hrvRmssd, setHrvRmssd] = useState(null);

    // Extra sensordata uit horloge / loopband / footpod (RSC 0x1814 & FTMS 0x1826)
    const [liveCadence, setLiveCadence] = useState(null);
    const [bleSpeedKmh, setBleSpeedKmh] = useState(null);
    const [blePaceSecPerKm, setBlePaceSecPerKm] = useState(null);
    const [bleDistanceKm, setBleDistanceKm] = useState(null);

    const [timeInZones, setTimeInZones] = useState({ 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 });

    const deviceRef = useRef(null);
    const samplesRef = useRef([]);
    const rrIntervalsRef = useRef([]);
    const currentBpmRef = useRef(0);
    const manualDisconnectRef = useRef(false);

    // 1. Parse Heart Rate Measurement (0x2A37)
    const handleHeartRateNotification = useCallback((event) => {
        const value = event.target.value;
        if (!value || value.byteLength < 2) return;

        const flags = value.getUint8(0);
        const is16Bit = (flags & 0x01) !== 0;
        const contactSupported = (flags & 0x04) !== 0;
        const contactDetected = (flags & 0x02) !== 0;
        const hasEnergyExpended = (flags & 0x08) !== 0;
        const hasRrInterval = (flags & 0x10) !== 0;

        if (contactSupported) {
            setSensorContact(contactDetected);
        }

        let offset = 1;
        let bpm = 0;
        if (is16Bit && value.byteLength >= 3) {
            bpm = value.getUint16(offset, true);
            offset += 2;
        } else {
            bpm = value.getUint8(offset);
            offset += 1;
        }

        if (hasEnergyExpended && value.byteLength >= offset + 2) {
            offset += 2;
        }

        if (hasRrInterval) {
            while (offset + 1 < value.byteLength) {
                const rawRr = value.getUint16(offset, true);
                const rrMs = (rawRr / 1024) * 1000;
                offset += 2;
                if (rrMs > 280 && rrMs < 2000) {
                    rrIntervalsRef.current.push(rrMs);
                    if (rrIntervalsRef.current.length > 30) rrIntervalsRef.current.shift();
                }
            }
            if (rrIntervalsRef.current.length >= 5) {
                const rrs = rrIntervalsRef.current;
                let sumSqDiff = 0;
                for (let i = 1; i < rrs.length; i++) {
                    const diff = rrs[i] - rrs[i - 1];
                    sumSqDiff += diff * diff;
                }
                setHrvRmssd(Math.round(Math.sqrt(sumSqDiff / (rrs.length - 1))));
            }
        }

        if (bpm > 30 && bpm < 245) {
            currentBpmRef.current = bpm;
            setCurrentBpm(bpm);
            samplesRef.current.push(bpm);
            const sum = samplesRef.current.reduce((a, b) => a + b, 0);
            setAvgBpm(Math.round(sum / samplesRef.current.length));
            setMaxBpm(prev => Math.max(prev, bpm));
        }
    }, []);

    // 2. Parse Running Speed & Cadence (0x2A53) - o.a. Garmin "Virtual Run", Zwift RunPod, Stryd, Polar
    const handleRscNotification = useCallback((event) => {
        try {
            const value = event.target.value;
            const flags = value.getUint8(0);
            const hasStrideLength = (flags & 0x01) !== 0;
            const hasTotalDistance = (flags & 0x02) !== 0;

            const rawSpeed = value.getUint16(1, true); // 1/256 m/s
            const speedMetersPerSec = rawSpeed / 256;
            const cadence = value.getUint8(3);

            let offset = 4;
            if (hasStrideLength && value.byteLength >= offset + 2) {
                offset += 2;
            }
            if (hasTotalDistance && value.byteLength >= offset + 4) {
                const distDecimeters = value.getUint32(offset, true); // in 1/10 meter
                const distKm = Math.round((distDecimeters / 10000) * 100) / 100;
                setBleDistanceKm(distKm);
            }

            if (cadence > 0) {
                // BLE RSC stuurt vaak strides/min (1 been); als < 115 vermenigvuldig met 2 voor SPM
                setLiveCadence(cadence < 115 ? cadence * 2 : cadence);
            }
            if (speedMetersPerSec > 0.4) {
                const kmh = Math.round(speedMetersPerSec * 3.6 * 10) / 10;
                const secPerKm = Math.round(1000 / speedMetersPerSec);
                setBleSpeedKmh(kmh);
                setBlePaceSecPerKm(secPerKm);
            }
        } catch (e) {}
    }, []);

    // 3. Parse Fitness Machine Treadmill Data (0x2ACD) voor slimme loopbanden
    const handleTreadmillNotification = useCallback((event) => {
        try {
            const value = event.target.value;
            if (value.byteLength >= 4) {
                const rawSpeed = value.getUint16(2, true); // 0.01 km/h
                const kmh = rawSpeed / 100;
                if (kmh > 1 && kmh < 35) {
                    setBleSpeedKmh(Math.round(kmh * 10) / 10);
                    setBlePaceSecPerKm(Math.round(3600 / kmh));
                }
            }
        } catch (e) {}
    }, []);

    useEffect(() => {
        if (!isConnected) return;
        const interval = setInterval(() => {
            const bpm = currentBpmRef.current;
            if (bpm > 40) {
                const zInfo = getHrZoneInfo(bpm, maxHr);
                if (zInfo.zone >= 1 && zInfo.zone <= 5) {
                    setTimeInZones(prev => ({ ...prev, [zInfo.zone]: (prev[zInfo.zone] || 0) + 1 }));
                }
            }
        }, 1000);
        return () => clearInterval(interval);
    }, [isConnected, maxHr]);

    const setupGattServices = useCallback(async (device) => {
        const server = await device.gatt.connect();
        let anyStreamBound = false;

        // A. Heart Rate Service (0x180D)
        try {
            const hrService = await server.getPrimaryService('heart_rate');
            const hrChar = await hrService.getCharacteristic('heart_rate_measurement');
            await hrChar.startNotifications();
            hrChar.addEventListener('characteristicvaluechanged', handleHeartRateNotification);
            anyStreamBound = true;
        } catch (e) {}

        // B. Running Speed & Cadence (0x1814 - Garmin Virtual Run / Footpods)
        try {
            const rscService = await server.getPrimaryService('running_speed_and_cadence');
            const rscChar = await rscService.getCharacteristic('rsc_measurement');
            await rscChar.startNotifications();
            rscChar.addEventListener('characteristicvaluechanged', handleRscNotification);
            anyStreamBound = true;
        } catch (e) {}

        // C. Fitness Machine Service (0x1826 - Loopbanden / Concept2 / Bikes)
        try {
            const ftmsService = await server.getPrimaryService('fitness_machine');
            try {
                const treadChar = await ftmsService.getCharacteristic('treadmill_data');
                await treadChar.startNotifications();
                treadChar.addEventListener('characteristicvaluechanged', handleTreadmillNotification);
                anyStreamBound = true;
            } catch (e) {}
        } catch (e) {}

        // D. Batterij Service (0x180F)
        try {
            const battService = await server.getPrimaryService('battery_service');
            const battChar = await battService.getCharacteristic('battery_level');
            const battVal = await battChar.readValue();
            setBatteryLevel(battVal.getUint8(0));
        } catch (e) {}

        return anyStreamBound;
    }, [handleHeartRateNotification, handleRscNotification, handleTreadmillNotification]);

    // Brede koppel-functie:
    // broadScan = false -> Zoekt gericht op services EN bekende horloge-namen (Garmin, Fenix, Polar, Wahoo, Coros, etc.)
    // broadScan = true  -> acceptAllDevices: true (toont ALLE Bluetooth apparaten in de buurt als het horloge zijn naam/UUID verbergt)
    const connectHeartRate = async (broadScan = false) => {
        if (!isSupported) {
            showToast('Web Bluetooth wordt niet ondersteund in deze browser. Gebruik Chrome op Android of Desktop.', 'error');
            return;
        }

        try {
            setIsConnecting(true);
            manualDisconnectRef.current = false;

            const requestOptions = broadScan
                ? {
                    acceptAllDevices: true,
                    optionalServices: OPTIONAL_BLE_SERVICES
                }
                : {
                    filters: [
                        { services: ['heart_rate'] },
                        { services: ['running_speed_and_cadence'] },
                        { services: ['fitness_machine'] },
                        { namePrefix: 'Garmin' },
                        { namePrefix: 'fēnix' },
                        { namePrefix: 'Fenix' },
                        { namePrefix: 'fenix' },
                        { namePrefix: 'Forerunner' },
                        { namePrefix: 'Instinct' },
                        { namePrefix: 'Venu' },
                        { namePrefix: 'epix' },
                        { namePrefix: 'HRM' },
                        { namePrefix: 'Polar' },
                        { namePrefix: 'Wahoo' },
                        { namePrefix: 'TICKR' },
                        { namePrefix: 'COROS' },
                        { namePrefix: 'Suunto' },
                        { namePrefix: 'WHOOP' },
                        { namePrefix: 'PM5' }
                    ],
                    optionalServices: OPTIONAL_BLE_SERVICES
                };

            const device = await navigator.bluetooth.requestDevice(requestOptions);
            deviceRef.current = device;
            setDeviceName(device.name || 'Sporthorloge / Sensor');

            device.addEventListener('gattserverdisconnected', async () => {
                if (manualDisconnectRef.current) {
                    setIsConnected(false);
                    setCurrentBpm(0);
                    currentBpmRef.current = 0;
                    return;
                }
                // Automatische Reconnect poging (tot 3x) als horloge kort buiten bereik was
                for (let attempt = 1; attempt <= 3; attempt++) {
                    try {
                        await new Promise(r => setTimeout(r, 1500 * attempt));
                        if (manualDisconnectRef.current || !deviceRef.current) break;
                        const ok = await setupGattServices(deviceRef.current);
                        if (ok) {
                            setIsConnected(true);
                            showToast(`Herverbonden met ${device.name || 'sensor'}`, 'success');
                            return;
                        }
                    } catch (e) {}
                }
                setIsConnected(false);
                setCurrentBpm(0);
                currentBpmRef.current = 0;
                showToast('Bluetooth verbinding verbroken.', 'info');
            });

            const boundOk = await setupGattServices(device);
            if (!boundOk) {
                showToast(
                    `Verbonden met ${device.name || 'apparaat'}, maar nog geen hartslag-uitzending gevonden. Zet op je horloge 'Zend hartslag uit' (Broadcast HR) aan!`,
                    'info'
                );
            } else {
                showToast(`Verbonden met ${device.name || 'Bluetooth Sensor'}`, 'success');
            }
            setIsConnected(true);
        } catch (err) {
            if (err && err.name !== 'NotFoundError') {
                showToast('Koppelen mislukt. Zorg dat "Zend hartslag uit" op je horloge aan staat.', 'error');
            }
        } finally {
            setIsConnecting(false);
        }
    };

    const disconnectHeartRate = useCallback(() => {
        manualDisconnectRef.current = true;
        try {
            if (deviceRef.current && deviceRef.current.gatt && deviceRef.current.gatt.connected) {
                deviceRef.current.gatt.disconnect();
            }
        } catch (e) {}
        setIsConnected(false);
        setCurrentBpm(0);
        currentBpmRef.current = 0;
    }, []);

    useEffect(() => {
        return () => {
            manualDisconnectRef.current = true;
            if (deviceRef.current && deviceRef.current.gatt && deviceRef.current.gatt.connected) {
                try { deviceRef.current.gatt.disconnect(); } catch (e) {}
            }
        };
    }, []);

    const zoneInfo = getHrZoneInfo(currentBpm, maxHr);

    return {
        isSupported,
        isConnected,
        isConnecting,
        deviceName,
        batteryLevel,
        sensorContact,
        currentBpm,
        avgBpm,
        maxBpm,
        maxHr,
        hrvRmssd,
        liveCadence,
        bleSpeedKmh,
        blePaceSecPerKm,
        bleDistanceKm,
        zoneInfo,
        timeInZones,
        connectHeartRate,
        disconnectHeartRate
    };
};
